import React, { useRef, useContext, useState, useCallback, useEffect } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBBadge,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import ReactCountryFlag from 'react-country-flag';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useFileUpload } from '../hooks/useFileUpload';
import { DevContext } from '../../../../../App';

export default function HandoutStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);
  const fileInputRefDe = useRef(null);
  const fileInputRefEn = useRef(null);
  
  // Use ref to always have access to the current handout value
  const handoutRef = useRef(formData.handout);
  useEffect(() => {
    handoutRef.current = formData.handout;
  }, [formData.handout]);
  
  // Use the file upload hook
  const { 
    uploadLectureFile, 
    removeFile,
    getFileUrl,
    isUploading, 
    uploadError: hookUploadError,
    resetUploadState 
  } = useFileUpload();
  
  const [localUploadError, setLocalUploadError] = useState(null);
  const [currentUploadLanguage, setCurrentUploadLanguage] = useState(null);

  // Get validation info from schema
  const stepValidation = getStepValidation('handout');
  const requiredFields = getStepRequiredFields('handout');
  const handoutFieldValidation = getFieldValidation('handout');

  const processFile = useCallback(async (file, language) => {
    try {
      setLocalUploadError(null);
      setCurrentUploadLanguage(language);
      
      console.log(`Starting upload for ${language}, current handout:`, handoutRef.current);
      
      // Upload using the hook
      const uploadedFile = await uploadLectureFile(file, `handout_${language}`);
      
      if (uploadedFile) {
        console.log(`File uploaded for ${language}:`, uploadedFile);
        // Use the ref to get the most current value
        const currentHandout = handoutRef.current || {};
        const newHandout = {
          ...currentHandout,
          [language]: uploadedFile
        };
        console.log(`Updating handout with:`, newHandout);
        handleFieldChange('handout', newHandout);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setLocalUploadError(error.message || t('Upload failed. Please try again.'));
    } finally {
      setCurrentUploadLanguage(null);
    }
  }, [uploadLectureFile, handleFieldChange, t]);

  const handleFileSelect = (event, language) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file, language);
    }
  };

  const handleRemoveFile = async (language) => {
    try {
      const currentHandout = handoutRef.current || {};
      if (currentHandout[language]?.fileName) {
        await removeFile(currentHandout[language].fileName, 'protected');
      }
    } catch (error) {
      console.error('Error removing file:', error);
    }
    
    const currentHandouts = handoutRef.current || {};
    const newHandouts = { ...currentHandouts };
    delete newHandouts[language];
    
    handleFieldChange('handout', Object.keys(newHandouts).length > 0 ? newHandouts : null);
    setLocalUploadError(null);
    resetUploadState();
    
    if (language === 'de' && fileInputRefDe.current) {
      fileInputRefDe.current.value = '';
    } else if (language === 'en' && fileInputRefEn.current) {
      fileInputRefEn.current.value = '';
    }
  };

  const downloadFile = async (language) => {
    try {
      const url = await getFileUrl(formData.handout[language].fileName, 'protected');
      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };
  
  const onDropDe = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0], 'de');
    }
  }, [processFile]);
  
  const onDropEn = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0], 'en');
    }
  }, [processFile]);
  
  const { getRootProps: getRootPropsDe, getInputProps: getInputPropsDe, isDragActive: isDragActiveDe } = useDropzone({ 
    onDrop: onDropDe,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    disabled: isUploading && currentUploadLanguage === 'de'
  });
  
  const { getRootProps: getRootPropsEn, getInputProps: getInputPropsEn, isDragActive: isDragActiveEn } = useDropzone({ 
    onDrop: onDropEn,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    disabled: isUploading && currentUploadLanguage === 'en'
  });

  const handleContinue = () => {
    if (stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const handleSkip = () => {
    // Only allow skip if handout is not required
    if (!requiredFields.includes('handout') || stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = stepValidation.isValid;
  const canSkip = !requiredFields.includes('handout') || stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Handout')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {t('The handout will be made available to event participants.')}<br />
            {eventConfig?.lectureDocumentsEndDate && (
              <>
                {t('Presentation materials must be uploaded to the system by {{date}}. If no presentation materials have been uploaded by this date, the presentation will be removed from the program.', {
                  date: new Date(eventConfig.lectureDocumentsEndDate).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long', 
                    year: 'numeric'
                  })
                })}
              </>
            )}
          </MDBTypography>
          
          <MDBRow>
            {/* German Handout */}
            <MDBCol md="6" className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <ReactCountryFlag 
                  countryCode="DE" 
                  svg 
                  style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
                />
                <MDBTypography tag="h6" className="mb-0">
                  {t('German Handout')}
                </MDBTypography>
              </div>
              
              {!formData.handout?.de ? (
                <div {...getRootPropsDe()}>
                  <input {...getInputPropsDe()} />
                  <MDBCard 
                    className={`text-center p-3 cursor-pointer ${
                      isDragActiveDe ? 'border-primary' : 'border-dashed'
                    }`}
                    style={{ 
                      cursor: 'pointer',
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: isDragActiveDe ? '#0d6efd' : '#dee2e6',
                      backgroundColor: isDragActiveDe ? '#f8f9fa' : 'transparent',
                      minHeight: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <MDBCardBody>
                      {isUploading && currentUploadLanguage === 'de' ? (
                        <>
                          <MDBSpinner role="status" className="mb-2" size="sm" />
                          <MDBTypography tag="small" className="d-block">
                            {t('Uploading...')}
                          </MDBTypography>
                        </>
                      ) : (
                        <>
                          <MDBIcon 
                            icon="file-pdf" 
                            size="2x" 
                            className="text-muted mb-2"
                          />
                          <MDBTypography tag="small" className="d-block mb-1">
                            {isDragActiveDe ? t('Drop PDF here') : t('Upload PDF')}
                          </MDBTypography>
                          <MDBTypography tag="small" className="text-muted">
                            {t('Click or drag')}
                          </MDBTypography>
                        </>
                      )}
                    </MDBCardBody>
                  </MDBCard>
                </div>
              ) : (
                <MDBCard>
                  <MDBCardBody className="p-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <MDBIcon 
                          icon="file-pdf" 
                          className="text-danger me-2" 
                          size="lg"
                        />
                        <div>
                          <MDBTypography tag="small" className="mb-0 d-block">
                            {formData.handout.de.file?.name || formData.handout.de.fileName || 'PDF'}
                          </MDBTypography>
                          {formData.handout.de.file?.size && (
                            <MDBTypography tag="small" className="text-muted">
                              {(formData.handout.de.file.size / 1024 / 1024).toFixed(2)} MB
                            </MDBTypography>
                          )}
                        </div>
                      </div>
                      <div>
                        <MDBBtn
                          color="link"
                          size="sm"
                          onClick={() => downloadFile('de')}
                          className="p-1 me-1"
                        >
                          <MDBIcon icon="download" size="sm" />
                        </MDBBtn>
                        <MDBBtn
                          color="link"
                          size="sm"
                          onClick={() => handleRemoveFile('de')}
                          disabled={isUploading}
                          className="p-1 text-danger"
                        >
                          <MDBIcon icon="trash" size="sm" />
                        </MDBBtn>
                      </div>
                    </div>
                  </MDBCardBody>
                </MDBCard>
              )}
            </MDBCol>
            
            {/* English Handout */}
            <MDBCol md="6" className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <ReactCountryFlag 
                  countryCode="US" 
                  svg 
                  style={{ width: '1.5em', height: '1.5em', marginRight: '0.5rem' }}
                />
                <MDBTypography tag="h6" className="mb-0">
                  {t('English Handout')}
                </MDBTypography>
              </div>
              
              {!formData.handout?.en ? (
                <div {...getRootPropsEn()}>
                  <input {...getInputPropsEn()} />
                  <MDBCard 
                    className={`text-center p-3 cursor-pointer ${
                      isDragActiveEn ? 'border-primary' : 'border-dashed'
                    }`}
                    style={{ 
                      cursor: 'pointer',
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: isDragActiveEn ? '#0d6efd' : '#dee2e6',
                      backgroundColor: isDragActiveEn ? '#f8f9fa' : 'transparent',
                      minHeight: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <MDBCardBody>
                      {isUploading && currentUploadLanguage === 'en' ? (
                        <>
                          <MDBSpinner role="status" className="mb-2" size="sm" />
                          <MDBTypography tag="small" className="d-block">
                            {t('Uploading...')}
                          </MDBTypography>
                        </>
                      ) : (
                        <>
                          <MDBIcon 
                            icon="file-pdf" 
                            size="2x" 
                            className="text-muted mb-2"
                          />
                          <MDBTypography tag="small" className="d-block mb-1">
                            {isDragActiveEn ? t('Drop PDF here') : t('Upload PDF')}
                          </MDBTypography>
                          <MDBTypography tag="small" className="text-muted">
                            {t('Click or drag')}
                          </MDBTypography>
                        </>
                      )}
                    </MDBCardBody>
                  </MDBCard>
                </div>
              ) : (
                <MDBCard>
                  <MDBCardBody className="p-2">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <MDBIcon 
                          icon="file-pdf" 
                          className="text-danger me-2" 
                          size="lg"
                        />
                        <div>
                          <MDBTypography tag="small" className="mb-0 d-block">
                            {formData.handout.en.file?.name || formData.handout.en.fileName || 'PDF'}
                          </MDBTypography>
                          {formData.handout.en.file?.size && (
                            <MDBTypography tag="small" className="text-muted">
                              {(formData.handout.en.file.size / 1024 / 1024).toFixed(2)} MB
                            </MDBTypography>
                          )}
                        </div>
                      </div>
                      <div>
                        <MDBBtn
                          color="link"
                          size="sm"
                          onClick={() => downloadFile('en')}
                          className="p-1 me-1"
                        >
                          <MDBIcon icon="download" size="sm" />
                        </MDBBtn>
                        <MDBBtn
                          color="link"
                          size="sm"
                          onClick={() => handleRemoveFile('en')}
                          disabled={isUploading}
                          className="p-1 text-danger"
                        >
                          <MDBIcon icon="trash" size="sm" />
                        </MDBBtn>
                      </div>
                    </div>
                  </MDBCardBody>
                </MDBCard>
              )}
            </MDBCol>
          </MDBRow>
          
          <input
            ref={fileInputRefDe}
            type="file"
            accept=".pdf"
            onChange={(e) => handleFileSelect(e, 'de')}
            style={{ display: 'none' }}
          />
          <input
            ref={fileInputRefEn}
            type="file"
            accept=".pdf"
            onChange={(e) => handleFileSelect(e, 'en')}
            style={{ display: 'none' }}
          />
        </MDBCol>
      </MDBRow>

      {(hasFieldError('handout') || localUploadError || hookUploadError) && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {localUploadError || hookUploadError || getFieldError('handout')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Handout Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Is Handout Required:</strong>{' '}
                <MDBBadge color={requiredFields.includes('handout') ? 'warning' : 'info'}>
                  {requiredFields.includes('handout') ? 'Required by Schema' : 'Optional'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Current Handout:</strong>{' '}
                {formData.handout ? (
                  <>
                    {formData.handout.de && (
                      <>DE: {formData.handout.de.file?.name || formData.handout.de.fileName || 'PDF'} 
                      {formData.handout.de.file?.size && `(${(formData.handout.de.file.size / 1024 / 1024).toFixed(2)} MB)`}</>
                    )}
                    {formData.handout.de && formData.handout.en && ' | '}
                    {formData.handout.en && (
                      <>EN: {formData.handout.en.file?.name || formData.handout.en.fileName || 'PDF'} 
                      {formData.handout.en.file?.size && `(${(formData.handout.en.file.size / 1024 / 1024).toFixed(2)} MB)`}</>
                    )}
                  </>
                ) : (
                  'No handout selected'
                )}
              </div>
              
              <div className="mb-2">
                <strong>Upload Status:</strong>{' '}
                <MDBBadge color={isUploading ? 'info' : (localUploadError || hookUploadError) ? 'danger' : 'success'}>
                  {isUploading ? 'Uploading...' : (localUploadError || hookUploadError) ? 'Error' : 'Ready'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {handoutFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{handoutFieldValidation.field}</code>: {handoutFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Skip Button:</strong>{' '}
                <MDBBadge color={canSkip ? 'success' : 'warning'}>
                  {canSkip ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <div>
                {!requiredFields.includes('handout') && (
                  <MDBBtn 
                    color="light"
                    onClick={handleSkip}
                    className="me-2"
                    disabled={!canSkip}
                  >
                    {t('Skip')}
                  </MDBBtn>
                )}
                <MDBBtn 
                  onClick={handleContinue}
                  disabled={!canContinue}
                >
                  {t('Continue')}
                </MDBBtn>
              </div>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}